<script setup lang="ts">
import { PokerlySymbolBrandNegative, PokerlySymbolWhiteNegative } from '@/assets/pokerly/index.js';
import { flipCard, hoverBounce, initializeState, scaleUpDown } from '@/components/game/game-card/animations.js';
import {
    gameCardBackVariants,
    gameCardFrontVariants,
    type GameCardVariants,
} from '@/components/game/game-card/index.js';
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
import { cn } from '@/lib/utils.ts';

interface Props {
    value?: number | string | null;
    selected?: boolean;
    disableAnimations?: boolean;
    face?: 'up' | 'down';
    variant?: GameCardVariants['variant'];
    disabled?: boolean;
}

const MAX_VALUE_LENGTH = 4;
const props = withDefaults(defineProps<Props>(), {
    face: 'down',
    selected: false,
    disableAnimations: false,
    disabled: false,
});

const emit = defineEmits<{
    click: [value: string | number | undefined | null];
}>();

// Handles card scale when selected
const cardContainerRef = useTemplateRef('cardContainerRef');
// Handles card flip and bounce on hover
const cardRef = useTemplateRef('cardRef');

const hovered = ref(false);
const canSelect = computed(() => !props.disabled && props.selected);

// Truncate the value to 5 characters
const truncatedValue = computed(() => {
    if (typeof props.value === 'string' && props.value.length > MAX_VALUE_LENGTH) {
        return props.value.slice(0, MAX_VALUE_LENGTH);
    }
    if (typeof props.value === 'number') {
        return props.value.toString().slice(0, MAX_VALUE_LENGTH);
    }
    return props.value;
});

onMounted(() => {
    if (!cardRef.value || !cardContainerRef.value) return;
    initializeState(cardRef.value, cardContainerRef.value, {
        face: props.face,
        hovered: hovered.value,
        canSelect: canSelect.value,
    });
});

watch(
    () => props.face,
    (next) => {
        if (!cardRef.value) return;
        flipCard(cardRef.value, {
            face: next,
            animate: !props.disableAnimations,
        });
    },
);

watch(
    () => canSelect.value,
    () => {
        if (!cardContainerRef.value) return;
        scaleUpDown(cardContainerRef.value, {
            animate: !props.disableAnimations,
            shouldScale: canSelect.value,
        });
    },
);

watch(hovered, async (next, prev) => {
    if (!cardRef.value) return;

    await nextTick();
    // Only animate when hover state actually changes or when the card is selected and hovered again (to re-trigger the animation)
    if (next === prev || (next && props.selected)) return;

    hoverBounce(cardRef.value, {
        animate: !props.disableAnimations,
        shouldScale: next,
    });
});

function onClick() {
    if (props.disabled) return;
    emit('click', props.value);
}

function onHovered() {
    if (props.disabled) return;
    hovered.value = true;
}
</script>

<template>
    <div
        ref="cardContainerRef"
        @mouseenter="onHovered"
        @mouseleave="hovered = false"
        @pointerenter="onHovered"
        @pointerleave="hovered = false"
        @pointercancel="hovered = false"
        @click="onClick"
        :class="
            cn(
                'transform-[scale(var(--hoverS,1))]',
                'w-17.5',
                'h-25',
                'relative',
                'will-change-transform',
                'origin-bottom-center',
                'rounded-lg',
            )
        "
    >
        <div
            ref="cardRef"
            :class="
                cn(
                    'absolute',
                    'w-full',
                    'h-full',
                    'face-down-card',
                    'group/game-card',
                    'transform-3d',
                    'rounded-lg',
                    'will-change-transform',
                    'origin-bottom-center',
                    'transform-[perspective(900px)_rotateY(var(--flip,0deg))_scale(var(--hoverS,1))]',
                )
            "
            :data-selected="canSelect"
            :data-face="face"
            :data-value="value"
        >
            <div data-card-side="back" :class="cn(gameCardBackVariants({ variant }), 'card-back')">
                <PokerlySymbolWhiteNegative v-if="variant === 'brand'" class="size-8" />
                <PokerlySymbolBrandNegative v-else class="size-8" />
            </div>
            <div data-card-side="front" :class="cn(gameCardFrontVariants({ variant }), 'card-front')">
                <span class="paragraph-xl">
                    {{ truncatedValue }}
                </span>
                <span class="absolute top-1 left-1.5 paragraph-xs">
                    {{ truncatedValue }}
                </span>
                <span class="absolute bottom-1 right-1.5 paragraph-xs rotate-180">
                    {{ truncatedValue }}
                </span>
            </div>
        </div>
    </div>
</template>
